import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Row, Col, Input } from 'reactstrap';
import 'bootstrap/dist/css/bootstrap.css';
import '../Library/PortalDashboardTileStyles.css';
import DashboardTile from './DashboardTile';
// import PermissionWrapper from '../../../auth/PermissionWrapper';
import './Dashboards.css';
import * as utilities from '../../../utilities';
import CompanyContext from '../../../auth/CompanyContext';
import CRMContext from '../../../auth/CRMContext';
import Loading from '../../Loading/Loading';
import PermissionWrapper from '../../../auth/PermissionWrapper';

const normalizeValue = (value) => String(value || '').trim().toLowerCase();

const PortalDashboard = (props) => {
    const companyCtx = useContext(CompanyContext);
    const crmCtx = useContext(CRMContext);
    const companyPortalDatabase = props.companySettings?.CompanyPortalDatabase;
    const hasCompanyPortalDatabase = String(companyPortalDatabase || '').trim() !== '';
    const canUsePortalCRM = hasCompanyPortalDatabase && crmCtx?.crmEnabled === true;

    const [portalDashboards, setPortalDashboards] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [households, setHouseholds] = useState([]);
    const [pageSize, setPageSizeState] = useState(
        localStorage.getItem('homePageSize') != null ? parseInt(localStorage.getItem('homePageSize')) : 10
    );
    const [selectedHousehold, setSelectedHousehold] = useState('');
    const [dropdownHouseholds, setDropdownHouseholds] = useState([]);
    const [contactAccounts, setContactAccounts] = useState([]);
    const [contactAccountsLoaded, setContactAccountsLoaded] = useState(!props.contactGUID);
    const [loadedWidgetsCount, setLoadedWidgetsCount] = useState(0);
    const [totalWidgets] = useState(0);
    const [householdDataLoaded, setHouseholdDataLoaded] = useState(Array.isArray(props.householdRows));

    const callbackArrayRef = useRef([]);
    const selectedHouseholdRef = useRef(selectedHousehold);
    const householdsRef = useRef(households);
    const contactAccountsRef = useRef(contactAccounts);
    const contactAccountsLoadedRef = useRef(contactAccountsLoaded);
    const contactGUIDRef = useRef(props.contactGUID);
    const pendingNotifyRef = useRef(false);
    const prevPropsRef = useRef({
        companyPortalDatabase: props.companySettings?.CompanyPortalDatabase,
        contactGUID: props.contactGUID,
        contactAccountsVersion: props.contactAccountsVersion
    });

    useEffect(() => {
        selectedHouseholdRef.current = selectedHousehold;
    }, [selectedHousehold]);

    useEffect(() => {
        householdsRef.current = households;
    }, [households]);

    useEffect(() => {
        contactAccountsRef.current = contactAccounts;
    }, [contactAccounts]);

    useEffect(() => {
        contactAccountsLoadedRef.current = contactAccountsLoaded;
    }, [contactAccountsLoaded]);

    useEffect(() => {
        contactGUIDRef.current = props.contactGUID;
    }, [props.contactGUID]);

    const getUniqueHouseholds = useCallback((sourceHouseholds = []) => {
        return [...new Set((sourceHouseholds || []).map(h => h && h.household).filter(Boolean))];
    }, []);

    const getAllowedHouseholds = useCallback((allHouseholds = [], options = {}) => {
        const contactGUID = options.contactGUID !== undefined ? options.contactGUID : contactGUIDRef.current;
        const nextContactAccountsLoaded = options.contactAccountsLoaded !== undefined
            ? options.contactAccountsLoaded
            : contactAccountsLoadedRef.current;
        const nextContactAccounts = options.contactAccounts !== undefined
            ? options.contactAccounts
            : contactAccountsRef.current;

        if (!contactGUID) return allHouseholds;
        if (!nextContactAccountsLoaded) return [];

        const accountSet = new Set((nextContactAccounts || []).map(normalizeValue));
        return allHouseholds.filter(household => accountSet.has(normalizeValue(household)));
    }, []);

    const reconcileHouseholdSelection = useCallback((options = {}) => {
        const notifyCallbacks = !!options.notifyCallbacks;
        const sourceHouseholds = options.households !== undefined ? options.households : householdsRef.current;
        const currentSelection = options.selectedHousehold !== undefined ? options.selectedHousehold : selectedHouseholdRef.current;

        const allHouseholds = getUniqueHouseholds(sourceHouseholds);
        const allowedHouseholds = getAllowedHouseholds(allHouseholds, options);

        const nextSelectedHousehold = allowedHouseholds.includes(currentSelection)
            ? currentSelection
            : (allowedHouseholds[0] || '');

        setDropdownHouseholds(allowedHouseholds);

        if (notifyCallbacks) {
            if (nextSelectedHousehold === selectedHouseholdRef.current) {
                if (nextSelectedHousehold) {
                    callbackArrayRef.current.forEach(cb => cb());
                }
            } else {
                pendingNotifyRef.current = true;
            }
        }

        setSelectedHousehold(nextSelectedHousehold);
    }, [getUniqueHouseholds, getAllowedHouseholds]);

    const applyContactAccounts = useCallback((contactGUID, accounts = [], options = {}) => {
        const nextContactAccounts = (accounts || []).map(item => String(item || '').trim()).filter(Boolean);
        contactAccountsRef.current = nextContactAccounts;
        contactAccountsLoadedRef.current = true;
        setContactAccounts(nextContactAccounts);
        setContactAccountsLoaded(true);
        reconcileHouseholdSelection({
            contactGUID: contactGUID || null,
            contactAccounts: nextContactAccounts,
            contactAccountsLoaded: true,
            notifyCallbacks: !!options.notifyCallbacks
        });
    }, [reconcileHouseholdSelection]);

    const getContactAccounts = useCallback((contactGUID, options = {}) => {
        if (!contactGUID) {
            applyContactAccounts(null, [], options);
            return;
        }

        const externalAccounts = Array.isArray(options.contactAccounts)
            ? options.contactAccounts
            : (Array.isArray(props.selectedContactHouseholds) ? props.selectedContactHouseholds : null);

        if (Array.isArray(externalAccounts)) {
            if (props.contactAccountsLoading) {
                return;
            }
            applyContactAccounts(contactGUID, externalAccounts, options);
            return;
        }

        fetch(`/api/contactAccounts/${contactGUID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.clientToken || "",
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (results.success) {
                    const nextContactAccounts = (results.data || []).map(item => item.account).filter(Boolean);
                    applyContactAccounts(contactGUID, nextContactAccounts, { notifyCallbacks: true });
                } else {
                    utilities.addToast('Unable to load contact accounts.', 5, 'danger');
                    applyContactAccounts(contactGUID, [], options);
                }
            })
            .catch(() => {
                utilities.addToast('An error has occurred.', 5, 'danger');
                applyContactAccounts(contactGUID, [], options);
            });
    }, [props.clientToken, props.selectedContactHouseholds, props.contactAccountsLoading, companyCtx.company, applyContactAccounts]);

    const applyHouseholdRows = useCallback((nextHouseholds = []) => {
        householdsRef.current = nextHouseholds;
        setHouseholds(nextHouseholds);
        setHouseholdDataLoaded(true);
        reconcileHouseholdSelection({
            households: nextHouseholds,
            contactAccounts: contactAccountsRef.current,
            contactAccountsLoaded: contactAccountsLoadedRef.current,
            contactGUID: contactGUIDRef.current
        });
    }, [reconcileHouseholdSelection]);

    const getHouseholdInfo = useCallback(() => {
        if (Array.isArray(props.householdRows)) {
            applyHouseholdRows(props.householdRows);
            return;
        }
        if (!canUsePortalCRM) {
            applyHouseholdRows([]);
            return;
        }

        setIsLoading(true);
        fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/householdinfo`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.idToken || "",
                'X-Database': companyCtx.company
            }
        })
            .then(results => {
                if (results.status === 401 || results.status === 403) {
                    applyHouseholdRows([]);
                    return null;
                }
                return results.json();
            })
            .then(results => {
                if (!results) return;
                applyHouseholdRows(results.data || []);
            })
            .catch(() => {
                utilities.addToast('Unable to load households.', 5, 'danger');
                applyHouseholdRows([]);
            })
            .finally(() => setIsLoading(false));
    }, [props.idToken, props.householdRows, companyCtx.company, canUsePortalCRM, applyHouseholdRows]);

    const getPortalDashboards = useCallback(() => {
        utilities.log('Dashboard.js/getPortalDashboards');
        if (!canUsePortalCRM) {
            setPortalDashboards([]);
            return;
        }

        fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/snapshots`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.idToken || "",
                'X-Database': companyCtx.company
            }
        })
            .then(results => {
                if (results.status === 401 || results.status === 403) {
                    setPortalDashboards([]);
                    return null;
                }
                return results.json();
            })
            .then(results => {
                if (!results) return;
                if (results.success) {
                    setPortalDashboards(results.data.map(item => {
                        return {
                            Configuration: (item.configuration),
                            DashboardTileId: item.widgetId,
                            Description: item.description,
                            Module: item.module,
                            Name: item.name,
                            Order: item.order,
                            Types: item.types,
                            UserActive: item.userActive,
                            UserConfiguration: {},
                            UserOrder: null
                        };
                    }));
                } else {
                    utilities.addToast('Unable to load portal dashboards.', 5, 'danger');
                }
            })
            .catch(() => {
                utilities.addToast('An error has occurred.', 5, 'danger');
            });
    }, [props.idToken, companyCtx.company, canUsePortalCRM]);

    useEffect(() => {
        utilities.log('Dashboard.js/componentDidMount');
        if (canUsePortalCRM) {
            getPortalDashboards();
        }
        if (Array.isArray(props.householdRows)) {
            applyHouseholdRows(props.householdRows);
        } else if (canUsePortalCRM) {
            getHouseholdInfo();
        }
        if (props.contactGUID) {
            getContactAccounts(props.contactGUID);
        }
    }, [applyHouseholdRows, getContactAccounts, getHouseholdInfo, getPortalDashboards, canUsePortalCRM, props.contactGUID, props.householdRows]);

    useEffect(() => {
        if (Array.isArray(props.householdRows)) {
            applyHouseholdRows(props.householdRows);
        }
    }, [props.householdRows, applyHouseholdRows]);

    useEffect(() => {
        if (!props.contactGUID) return;
        if (!Array.isArray(props.selectedContactHouseholds)) return;
        getContactAccounts(props.contactGUID, {
            contactAccounts: props.selectedContactHouseholds,
            notifyCallbacks: true
        });
    }, [props.contactGUID, props.selectedContactHouseholds, getContactAccounts]);

    useEffect(() => {
        utilities.log('Dashboard.js/componentDidUpdate');
        const prevProps = prevPropsRef.current;

        if (
            companyPortalDatabase !== prevProps.companyPortalDatabase &&
            canUsePortalCRM
        ) {
            getPortalDashboards();
        }

        if (props.contactGUID !== prevProps.contactGUID) {
            const hasContactGUID = !!props.contactGUID;
            contactGUIDRef.current = props.contactGUID;
            contactAccountsRef.current = [];
            contactAccountsLoadedRef.current = !hasContactGUID;
            setSelectedHousehold('');
            setContactAccounts([]);
            setContactAccountsLoaded(!hasContactGUID);

            if (hasContactGUID) {
                getContactAccounts(props.contactGUID, {
                    contactAccounts: Array.isArray(props.selectedContactHouseholds) ? props.selectedContactHouseholds : undefined
                });
            } else {
                reconcileHouseholdSelection({
                    contactGUID: null,
                    selectedHousehold: '',
                    contactAccounts: [],
                    contactAccountsLoaded: true
                });
            }
        }

        if (
            props.contactGUID &&
            props.contactGUID === prevProps.contactGUID &&
            props.contactAccountsVersion !== prevProps.contactAccountsVersion
        ) {
            getContactAccounts(props.contactGUID, {
                contactAccounts: Array.isArray(props.selectedContactHouseholds) ? props.selectedContactHouseholds : undefined
            });
        }

        prevPropsRef.current = {
            companyPortalDatabase,
            contactGUID: props.contactGUID,
            contactAccountsVersion: props.contactAccountsVersion
        };
    }, [
        companyPortalDatabase,
        canUsePortalCRM,
        props.contactGUID,
        props.contactAccountsVersion,
        props.selectedContactHouseholds,
        getPortalDashboards,
        getContactAccounts,
        reconcileHouseholdSelection
    ]);

    useEffect(() => {
        if (pendingNotifyRef.current) {
            pendingNotifyRef.current = false;
            if (selectedHousehold) {
                callbackArrayRef.current.forEach(cb => cb());
            }
        }
    }, [selectedHousehold]);

    useEffect(() => {
        if (loadedWidgetsCount === totalWidgets && householdDataLoaded) {
            callbackArrayRef.current.forEach(cb => cb());
        }
    }, [loadedWidgetsCount, totalWidgets, householdDataLoaded]);

    const onWidgetLoad = useCallback(() => {
        setLoadedWidgetsCount(prevState => prevState + 1);
    }, []);

    const onAddCallback = useCallback((cb) => {
        callbackArrayRef.current.push(cb);
        if (selectedHouseholdRef.current) {
            cb();
        }
    }, []);

    const onUserChange = useCallback((e) => {
        const nextHousehold = e.target.value;
        if (nextHousehold === selectedHouseholdRef.current) {
            if (nextHousehold) {
                callbackArrayRef.current.forEach(cb => cb());
            }
            return;
        }
        pendingNotifyRef.current = true;
        setSelectedHousehold(nextHousehold);
    }, []);

    const setPageSize = (nextPageSize) => {
        setPageSizeState(nextPageSize);
        localStorage.setItem('homePageSize', nextPageSize);
    };

    const addCompany = () => {
        fetch('/api/demographics/', {
            method: 'POST',
            body: JSON.stringify({
                portalDashboards,
                isLoading,
                households,
                pageSize,
                selectedHousehold,
                dropdownHouseholds,
                contactAccounts,
                contactAccountsLoaded,
                loadedWidgetsCount,
                totalWidgets,
                householdDataLoaded
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then((results) => {
                if (results.success) {
                    localStorage.setItem('demographicsActiveTab', 2);
                    props.history.push({
                        pathname: `/demographics/${results.data}`,
                    });
                }
            });
    };

    void setPageSize;
    void addCompany;

    const isContactAccountsLoading = Boolean(props.contactAccountsLoading) || (!!props.contactGUID && !contactAccountsLoaded);
    const isHouseholdsLoading = Boolean(props.householdsLoading) || isLoading;
    const isMappingDataLoading = isContactAccountsLoading || isHouseholdsLoading;

    return (
        <PermissionWrapper {...props} allowedPermission="CRM Integration" hidden={true}>
            {/* // <PermissionWrapper hidden={true} refreshUser={props.refreshUser} {...props} allowedPermission="Home Dashboard Tiles"> */}
            {isMappingDataLoading ? (
                <Row className='mb-4'>
                    <Col className='text-muted'>
                        Loading mapped households...
                        <Loading />
                    </Col>
                </Row>
            ) : dropdownHouseholds.length > 0 ? (
                <Row className='mb-4'>
                    <Col>
                        <Input
                            type="select"
                            className='m-0'
                            value={selectedHousehold}
                            onChange={onUserChange}
                        >
                            {dropdownHouseholds.length > 1 && <option value="" disabled>
                                Select Household
                            </option>}
                            {dropdownHouseholds.map((household) => (
                                <option key={household} value={household}>
                                    {household}
                                </option>
                            ))}
                        </Input>
                    </Col>
                </Row>
            ) : null}
            {selectedHousehold !== '' && <Row className='dashboardTilesRow'>
                {[...portalDashboards].sort((a, b) => a.UserOrder - b.UserOrder).map((item) => (
                    <DashboardTile
                        {...props}
                        key={item.DashboardTileId}
                        id={item.DashboardTileId}
                        name={item.Name}
                        types={item.Types}
                        dashboardTile={item}
                        widths={(item.Configuration != null && item.Configuration.widths != null && item.Configuration.widths.length > 0) ? item.Configuration.widths : [6]}
                        width={4}
                        active={item.UserActive}
                        pageSize={pageSize}
                        selectedHousehold={selectedHousehold}
                        onAddCallback={onAddCallback}
                        onWidgetLoad={onWidgetLoad}
                    />
                ))}
            </Row>}
            {/* // </PermissionWrapper> */}
        </PermissionWrapper>
    );
};

export default PortalDashboard;
